import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdRefresh, MdSave } from 'react-icons/md';
import { useUser } from '../context/UserContext';
import {
  actualizarSubgerenteEjecutivoLote,
  listarEjecutivosSubgerente,
} from '../api/apiConfig';

const pick = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null && String(v).trim() !== '') return v;
  }
  return '';
};

const unwrapFilas = (response) => {
  let data = response;
  if (response?.statusCode && response.body != null) {
    data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
  }
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.filas)) return data.filas;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const unwrapSubgerentes = (response, filas) => {
  let data = response;
  if (response?.statusCode && response.body != null) {
    data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
  }
  const fromApi = Array.isArray(data?.subgerentes) ? data.subgerentes : [];
  const set = new Set(fromApi.map((n) => String(n).trim()).filter(Boolean));
  filas.forEach((r) => {
    const n = String(pick(r, 'SUBGERENTE_NOMBRE', 'subgerente_nombre')).trim();
    if (n) set.add(n);
  });
  return [...set].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
};

const rowId = (row) => Number(row?.id ?? row?.ID ?? 0);

const normalizeNombre = (value) => String(value ?? '').trim().slice(0, 150);

const MantenedorSubgerentes = () => {
  const { user } = useUser();
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [grupo, setGrupo] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('activos');
  const [soloSinSubgerente, setSoloSinSubgerente] = useState(false);
  const [bulkValue, setBulkValue] = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setMessage('');
    try {
      const resp = await listarEjecutivosSubgerente(user);
      if (resp?.ok === false) {
        throw new Error(resp?.error || resp?.message || 'No se pudo listar ejecutivos');
      }
      const filas = unwrapFilas(resp);
      setRows(filas);
      setCatalogo(unwrapSubgerentes(resp, filas));
      setDrafts({});
    } catch (err) {
      setRows([]);
      setCatalogo([]);
      setMessage(err?.message || 'No se pudieron cargar los ejecutivos');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const grupos = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const g = String(pick(r, 'GRUPO', 'grupo')).trim();
      if (g) set.add(g);
    });
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const currentValue = useCallback(
    (row) => {
      const id = rowId(row);
      if (Object.prototype.hasOwnProperty.call(drafts, id)) return drafts[id];
      return String(pick(row, 'SUBGERENTE_NOMBRE', 'subgerente_nombre'));
    },
    [drafts],
  );

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return rows.filter((r) => {
      const estado = Number(r.ESTADO ?? r.estado);
      if (estadoFiltro === 'activos' && estado !== 1) return false;
      if (estadoFiltro === 'inactivos' && estado === 1) return false;
      const g = String(pick(r, 'GRUPO', 'grupo'));
      if (grupo && g !== grupo) return false;
      const sub = normalizeNombre(currentValue(r));
      if (soloSinSubgerente && sub) return false;
      if (!q) return true;
      const blob = [
        pick(r, 'NOMBRE', 'nombre'),
        pick(r, 'MAIL', 'mail', 'email'),
        g,
        pick(r, 'SUCURSAL', 'sucursal'),
        pick(r, 'TIPO_EJECUTIVO', 'tipo_ejecutivo'),
        sub,
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rows, busqueda, grupo, estadoFiltro, soloSinSubgerente, currentValue]);

  const dirtyItems = useMemo(() => {
    return rows
      .filter((r) => Object.prototype.hasOwnProperty.call(drafts, rowId(r)))
      .map((r) => {
        const id = rowId(r);
        const original = normalizeNombre(pick(r, 'SUBGERENTE_NOMBRE', 'subgerente_nombre'));
        const next = normalizeNombre(drafts[id]);
        return { id, original, next, changed: original !== next };
      })
      .filter((x) => x.changed && x.id > 0);
  }, [rows, drafts]);

  const setDraft = (id, value) => {
    setDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const aplicarAVisibles = () => {
    const value = bulkValue;
    if (!visibles.length) {
      setMessage('No hay ejecutivos visibles para asignar.');
      setMessageType('error');
      return;
    }
    setDrafts((prev) => {
      const next = { ...prev };
      visibles.forEach((r) => {
        next[rowId(r)] = value;
      });
      return next;
    });
    setMessage(`Subgerente aplicado a ${visibles.length} ejecutivo(s) visible(s). Revisa y guarda.`);
    setMessageType('success');
  };

  const handleSave = async () => {
    if (!dirtyItems.length || saving) return;
    setSaving(true);
    setMessage('');
    try {
      const resp = await actualizarSubgerenteEjecutivoLote(user, {
        items: dirtyItems.map((x) => ({
          id: x.id,
          subgerente_nombre: x.next || null,
        })),
      });
      if (resp?.ok === false) {
        throw new Error(resp?.error || resp?.message || 'No se pudo guardar');
      }
      const updated = Number(resp?.updated || dirtyItems.length);
      setMessage(`Se actualizó el subgerente en ${updated} ejecutivo(s).`);
      setMessageType('success');
      await load();
    } catch (err) {
      setMessage(err?.message || 'No se pudo guardar el subgerente');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const opcionesSubgerente = useMemo(() => {
    const set = new Set(catalogo);
    Object.values(drafts).forEach((v) => {
      const n = normalizeNombre(v);
      if (n) set.add(n);
    });
    return [...set].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [catalogo, drafts]);

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">Mantenedor subgerente</h1>
        <p className="text-gray-600">Asigna o modifica `SUBGERENTE_NOMBRE` en ejecutivos_estado</p>
        <Link
          to="/panel-marketing/carga-meta-volumen"
          className="btn-primary text-sm py-2 px-3"
          style={{ textDecoration: 'none' }}
        >
          Carga meta volumen
        </Link>
      </div>

      <div className="seccion-datos">
        <p className="text-sm text-gray-600 mb-4">
          Elige un subgerente existente o escribe un nombre nuevo (máx. 150 caracteres). Deja el campo vacío para quitar la asignación.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <label className="text-sm text-gray-700">
            Buscar
            <input
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre, correo, grupo o subgerente"
            />
          </label>
          <label className="text-sm text-gray-700">
            Grupo
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={grupo}
              onChange={(e) => setGrupo(e.target.value)}
            >
              <option value="">Todos</option>
              {grupos.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Estado
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
            >
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
              <option value="todos">Todos</option>
            </select>
          </label>
          <label className="text-sm text-gray-700 flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              checked={soloSinSubgerente}
              onChange={(e) => setSoloSinSubgerente(e.target.checked)}
            />
            Solo sin subgerente
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="text-sm text-gray-700">
            Asignar a visibles
            <input
              className="mt-1 block w-64 border border-gray-300 rounded-md px-3 py-2 text-sm"
              list="catalogo-subgerentes"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              placeholder="Nombre del subgerente"
              maxLength={150}
            />
          </label>
          <button type="button" className="btn-primary py-2 px-4 text-sm" onClick={aplicarAVisibles}>
            Aplicar a visibles
          </button>
          <button type="button" className="btn-primary py-2 px-4 text-sm" onClick={load} disabled={loading}>
            <span className="inline-flex items-center gap-1">
              <MdRefresh size={16} /> Recargar
            </span>
          </button>
          <button
            type="button"
            className="btn-success py-2 px-4 text-sm disabled:opacity-50"
            onClick={handleSave}
            disabled={!dirtyItems.length || saving}
          >
            <span className="inline-flex items-center gap-1">
              <MdSave size={16} />
              {saving ? 'Guardando...' : `Guardar cambios (${dirtyItems.length})`}
            </span>
          </button>
        </div>

        {message && (
          <div
            className={`mb-4 p-3 rounded text-sm ${
              messageType === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}
          >
            {message}
          </div>
        )}

        <datalist id="catalogo-subgerentes">
          {opcionesSubgerente.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>

        {loading ? (
          <p className="text-gray-500">Cargando ejecutivos...</p>
        ) : (
          <div className="tabla-container overflow-x-auto" style={{ maxHeight: '70vh' }}>
            <table className="w-full text-sm" style={{ minWidth: '920px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left' }}>Ejecutivo</th>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left' }}>Correo</th>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left' }}>Grupo</th>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left' }}>Sucursal</th>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'center' }}>Estado</th>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left' }}>Subgerente</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((r) => {
                  const id = rowId(r);
                  const dirty = Object.prototype.hasOwnProperty.call(drafts, id)
                    && normalizeNombre(drafts[id]) !== normalizeNombre(pick(r, 'SUBGERENTE_NOMBRE', 'subgerente_nombre'));
                  const estado = Number(r.ESTADO ?? r.estado);
                  return (
                    <tr key={id || pick(r, 'MAIL', 'mail')}>
                      <td className="font-medium whitespace-nowrap">{pick(r, 'NOMBRE', 'nombre') || '—'}</td>
                      <td className="whitespace-nowrap text-gray-600">{pick(r, 'MAIL', 'mail', 'email') || '—'}</td>
                      <td className="whitespace-nowrap text-gray-600">{pick(r, 'GRUPO', 'grupo') || '—'}</td>
                      <td className="whitespace-nowrap text-gray-600">{pick(r, 'SUCURSAL', 'sucursal') || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={estado === 1 ? 'text-emerald-700' : 'text-gray-400'}>
                          {estado === 1 ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td style={{ backgroundColor: dirty ? '#fef3c7' : undefined }}>
                        <input
                          className="w-full border border-transparent hover:border-gray-300 focus:border-blue-400 rounded px-2 py-1 bg-transparent"
                          list="catalogo-subgerentes"
                          value={currentValue(r)}
                          onChange={(e) => setDraft(id, e.target.value)}
                          placeholder="Sin asignar"
                          maxLength={150}
                        />
                      </td>
                    </tr>
                  );
                })}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-6">
                      No hay ejecutivos para los filtros seleccionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-gray-500 mt-3">
          {visibles.length} visible{visibles.length === 1 ? '' : 's'} de {rows.length} ejecutivo{rows.length === 1 ? '' : 's'}.
        </p>
      </div>
    </div>
  );
};

export default MantenedorSubgerentes;
